import getDesktopDownloadUrl from '../utils/desktop-dowload-url';
import {
  DEFAULT_DE_LOCALE,
  DEFAULT_ES_LOCALE,
  DEFAULT_FR_LOCALE,
  DEFAULT_KO_LOCALE,
  DEFAULT_LOCALE,
  ITUNES_APP_LINK,
  LANGUAGE_NAMES,
  PLAY_STORE_APP_LINK,
} from '../constants';
import { createSelector } from 'reselect';
import { splitLocale } from '../lib/locale-utils';
import countryToCurrency from 'country-to-currency';
import { selectPlayerIsPlayingAd } from './player';
import { selectShouldPlayPreviews } from './features';
import { get } from 'lodash';

export function selectLocale(state) {
  return state.client.locale;
}

export function selectIsI18nEnabled(state) {
  return state.client.supportedLocales.length > 1;
}

export function selectUserAgentInfo(state) {
  return state.client.userAgentInfo;
}

export function selectUserAgent(state) {
  return get(selectUserAgentInfo(state), 'source');
}

export function selectIsMobile(state) {
  return get(selectUserAgentInfo(state), 'isMobile');
}

export function selectIsDesktop(state) {
  return get(selectUserAgentInfo(state), 'isDesktop');
}

export function selectIsAndroid(state) {
  const userAgentInfo = selectUserAgentInfo(state);
  return get(userAgentInfo, 'isAndroid') || get(userAgentInfo, 'isAndroidTablet');
}

export function selectIsIOS(state) {
  const userAgentInfo = selectUserAgentInfo(state);
  return (
    get(userAgentInfo, 'isiPhone') || get(userAgentInfo, 'isiPad') || get(userAgentInfo, 'isiPod')
  );
}

export function selectIsWindows(state) {
  return get(selectUserAgentInfo(state), 'isWindows');
}

export function selectIsMac(state) {
  return get(selectUserAgentInfo(state), 'isMac');
}

export function selectIsFirefox(state) {
  return get(selectUserAgentInfo(state), 'isFirefox');
}

export function selectIsChrome(state) {
  return get(selectUserAgentInfo(state), 'isChrome');
}

// capacitor and ios webview are considered in-app
export function selectIsInApp(state) {
  return state.client.isInApp;
}

export function selectIsInIOSApp(state) {
  return selectIsInApp(state) && !__CAPACITOR__;
}

export function selectChrome(state) {
  return state.client.ui.chrome;
}

export function selectSupportedLocales(state) {
  return state.client.supportedLocales;
}

export function selectIsSupportedLocale(state, locale) {
  return selectSupportedLocales(state).indexOf(locale) !== -1;
}

export function selectIsConnected(state) {
  return state.client.connected;
}

export const selectSupportedLanguages = createSelector(
  [selectLocale, selectSupportedLocales],
  (geoDetectedLocale, supportedLocales) => {
    const defaultLocales = {
      en: DEFAULT_LOCALE,
      de: DEFAULT_DE_LOCALE,
      fr: DEFAULT_FR_LOCALE,
      es: DEFAULT_ES_LOCALE,
      ko: DEFAULT_KO_LOCALE,
    };

    const supportedLanguages = supportedLocales.reduce((result, locale) => {
      const code = splitLocale(locale).language;
      const preferredLocale =
        geoDetectedLocale.indexOf(code) !== -1 ? geoDetectedLocale : defaultLocales[code];

      result[code] = result[code] || {
        code,
        preferredLocale,
        title: LANGUAGE_NAMES[code],
      };

      return result;
    }, {});

    return Object.values(supportedLanguages);
  }
);

export function selectIsLoadingAudio(state) {
  return state.client.isLoadingAudio;
}

export function selectServiceWorkerSupportedBrowsers(state) {
  // Only Chrome desktop supports service worker encryption. Other clients only support HLS encryption.
  // If you want to enable/disable service worker encryption for specific clients, set it here.
  // If you disable service worker encryption, it will then use HLS player.
  const isChrome = selectIsChrome(state);
  const isDesktop = selectIsDesktop(state);
  return isDesktop && isChrome;
}

export function selectHLSSupportedBrowsers() {
  // Most browsers support HLS encryption.
  // If you want to enable/disable HLS encryption for specific clients, set it here.
  // If you disable HLS encryption, it will then use HLS unencrypted player.
  return true;
}

export function selectEncryptionEnabled(state) {
  // If you want to disable encryption for all clients, set the env var `ENCRYPTION_ENABLED` to 0.
  return state.client.encryptedPlayback;
}

export function selectUseMP3EncryptedPlayer(state) {
  return selectEncryptionEnabled(state) && selectServiceWorkerSupportedBrowsers(state);
}

export function selectUseM3U8EncryptedPlayer(state) {
  return selectEncryptionEnabled(state) && selectHLSSupportedBrowsers(state);
}

export function selectDeviceId(state) {
  return state.client.deviceId;
}

export function selectElectronClientVersion(state) {
  return state.client.versions.electron;
}

export function selectWebappClientVersion(state) {
  return state.client.versions.web;
}

export function selectSignupUserAgent(state, isElectron) {
  if (isElectron) {
    return `desktop v${selectElectronClientVersion(state)}`;
  }
  return `web v${selectWebappClientVersion(state)}`;
}

export function selectRecentlyPlayedItems(state) {
  return state.recentlyPlayed.entities;
}

export function selectCurrentRoutePath(state) {
  return state.routing.locationBeforeTransitions.pathname;
}

export function selectRecentlyPlayedEnabled(state) {
  return state.client.recentlyPlayedEnabled;
}

export function selectCurrentClientDesktopDownloadUrl(state) {
  const electronVersion = selectElectronClientVersion(state);
  const isMac = selectIsMac(state);
  const isWindows = selectIsWindows(state);

  return getDesktopDownloadUrl(electronVersion, isMac, isWindows);
}

export const selectClientPlatformUrls = createSelector(
  [state => selectElectronClientVersion(state)],
  electronVersion => {
    const macDownloadUrl = getDesktopDownloadUrl(electronVersion, true, false);
    const windowsDownloadUrl = getDesktopDownloadUrl(electronVersion, false, true);
    return [
      {
        name: 'macOS',
        url: macDownloadUrl,
      },
      {
        name: 'Windows',
        url: windowsDownloadUrl,
      },
      {
        name: 'iOS',
        url: ITUNES_APP_LINK,
      },
      {
        name: 'Android',
        url: PLAY_STORE_APP_LINK,
      },
    ];
  }
);

export function selectPersonalPlaylistToAddState(state) {
  return state.client.ui.personalPlaylistModals.addToPlaylistModal;
}

export function selectPersonalPlaylistToEdit(state) {
  return state.client.ui.personalPlaylistModals.editPlaylistModal.playlist;
}

export function selectPersonalPlaylistToDelete(state) {
  return state.client.ui.personalPlaylistModals.deletePlaylistModal.playlist;
}

export function selectDeleteTracksModalState(state) {
  return state.client.ui.deleteTracksModal;
}

export function selectClientType() {
  if (__ELECTRON__) {
    return 'desktop';
  }
  return 'web';
}

export function selectClientPlatform(state) {
  if (selectIsInIOSApp(state)) {
    return 'ios';
  }
  if (__CAPACITOR__) {
    return 'android';
  }
  if (__ELECTRON__) {
    return 'desktop';
  }
  return 'web';
}

export function selectClientDeviceClass(state) {
  const userAgentInfo = selectUserAgentInfo(state);
  if (get(userAgentInfo, 'isMobile')) {
    return 'phone';
  }
  if (get(userAgentInfo, 'isTablet')) {
    return 'tablet';
  }
  if (get(userAgentInfo, 'isSmartTV')) {
    return 'tv';
  }
  return 'computer';
}

export function selectClientName(state) {
  // Order is important, macOS will be true when iOS is true, but not vice-versa
  if (selectIsIOS(state)) {
    return 'iOS';
  }

  if (selectIsAndroid(state)) {
    return 'Android';
  }

  if (selectIsWindows(state)) {
    return 'Windows';
  }

  if (selectIsMac(state)) {
    return 'macOS';
  }

  return 'Windows'; // default to windows
}

export function selectGlobalTrackingProps(state) {
  return {
    ctxClientType: selectClientType(state),
    ctxDeviceClass: selectClientDeviceClass(state),
    ctxPlatform: selectClientPlatform(state),
  };
}

export function selectClientDownloadUrl(state) {
  const clientName = selectClientName(state);
  const platformUrls = selectClientPlatformUrls(state);
  const platform = platformUrls.find(item => item.name === clientName);

  return platform.url;
}

export function selectClientCountry(state) {
  return state.client.country;
}

export function selectClientIP(state) {
  return state.client.ip;
}

export function selectIsGeoBlockedCountry(state) {
  return state.client.shouldBlock;
}

export function selectAuthRequired(state) {
  return state.client.authRequired;
}

export function selectCurrency(state) {
  return state.client.currency;
}

export function selectShouldShowCurrencyNote(state) {
  const displayedCurrency = selectCurrency(state);
  const country = selectClientCountry(state);
  const localCurrency = countryToCurrency[country];
  return Boolean(localCurrency) && localCurrency !== displayedCurrency;
}

export function selectSepaIsSupported() {
  // Temporarily disabled on November 2020
  // https://idagio.slack.com/archives/C04RFTRT5/p1605692014002500
  return false;
}

export function selectAuthApiBase(state) {
  return state.client.urls.authApiBase;
}

export function selectFonts(state) {
  return state.client.fonts;
}

export function selectBaseUrl(state) {
  return state.client.urls.baseUrl;
}

export function selectTopDomain(state) {
  return state.client.urls.topDomain;
}

export function selectSegmentCdnHost(state, options = { noProtocol: false }) {
  const url = state.client.urls.segmentCdnHost;
  if (options.noProtocol) {
    return url.replace(/(^\w+:|^)\/\//, '');
  }
  return url;
}

export function selectEasyConnectSdkOriginUrl(state) {
  return state.client.urls.easyConnectSdkUrlOrigin;
}

export function selectEasyConnectPartnerId(state) {
  return state.client.easyConnectPartnerId;
}

export function selectAdobeApiKey(state) {
  return state.client.adobeApiKey;
}

export function selectSegmentWriteKey(state) {
  return state.client.segmentWriteKey;
}

export function selectTheme(state) {
  return state.client.ui.theme;
}

export function selectWindowInsets(state) {
  return state.client.ui.windowInsets;
}

export function selectAutoplay(state) {
  return state.client.ui.autoplay;
}

export function selectUrlToFonts(state) {
  return state.client.fonts.url;
}

export function selectPlayerAudioQualityDialogIsOpen(state) {
  return state.client.ui.playerAudioQualityDialogIsOpen;
}

export function selectPlayerExternalDeviceDialogIsOpen(state) {
  return state.client.ui.playerExternalDeviceDialogIsOpen;
}

export function selectPlayerBarInfoHidden(state) {
  const isPlayingAd = selectPlayerIsPlayingAd(state);
  return !isPlayingAd && state.client.ui.playerbarInfoHidden;
}

export function selectShouldShowPreviewsModal(state) {
  return selectShouldPlayPreviews(state) && state.client.ui.previewsModal.showingsNumber < 2;
}

export function selectPageTrackingContext(state) {
  return state.client.ui.pageTrackingContext;
}

export function selectModalType(state) {
  return state.client.ui.modalType;
}

export function selectHttpRequestPostData(state) {
  return state.client.httpRequest.postData;
}

export function selectCookiePreferences(state) {
  return state.client.cookiePreferences;
}

export function selectTicketPurchaseIsDisabled(state) {
  return state.client.ticketPurchaseIsDisabled;
}

export function selectCaptchaType(state) {
  return state.client.captchas.captchaType;
}

export function selectCaptchaSiteKey(state) {
  return state.client.captchas.captchaSiteKey;
}

export function selectHelmet(state) {
  return state.client.helmet;
}
